import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { User, Building, Mail, Phone, Lock, Check } from 'lucide-react';

export const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    contact_number: '',
    password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirm_password, ...registerData } = formData;
      await register(registerData);
      toast.success('Registration successful! Please wait for admin approval.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-semibold mb-4" data-testid="register-heading">Create Your Account</h1>
          <p className="text-muted-foreground">Register to access premium diamond pricing and catalog</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm uppercase tracking-widest font-medium" htmlFor="first_name">
                First Name *
              </label>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none pl-8 py-3 transition-colors"
                  placeholder="John"
                  data-testid="register-firstname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm uppercase tracking-widest font-medium" htmlFor="last_name">
                Last Name *
              </label>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none pl-8 py-3 transition-colors"
                  placeholder="Doe"
                  data-testid="register-lastname-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm uppercase tracking-widest font-medium" htmlFor="company_name">
              Company Name *
            </label>
            <div className="relative">
              <Building className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                id="company_name"
                name="company_name"
                type="text"
                value={formData.company_name}
                onChange={handleChange}
                required
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none pl-8 py-3 transition-colors"
                placeholder="Your Jewelry Company"
                data-testid="register-company-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm uppercase tracking-widest font-medium" htmlFor="email">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none pl-8 py-3 transition-colors"
                  placeholder="your@email.com"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm uppercase tracking-widest font-medium" htmlFor="contact_number">
                Contact Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  id="contact_number"
                  name="contact_number"
                  type="tel"
                  value={formData.contact_number}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none pl-8 py-3 transition-colors"
                  placeholder="+1 234 567 8900"
                  data-testid="register-contact-input"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm uppercase tracking-widest font-medium" htmlFor="password">
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none pl-8 py-3 transition-colors"
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm uppercase tracking-widest font-medium" htmlFor="confirm_password">
                Confirm Password *
              </label>
              <div className="relative">
                <Check className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none pl-8 py-3 transition-colors"
                  placeholder="••••••••"
                  data-testid="register-confirmpassword-input"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4 transition-all duration-300 disabled:opacity-50"
            data-testid="register-submit-btn"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="register-login-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};